import { useState, useCallback, useEffect, useContext } from 'react'
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Button,
  Box,
  CircularProgress,
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import DownloadIcon from '@mui/icons-material/Download'

import { AlertContext } from 'src/context/AlertContext'
import { ROUTES } from 'src/routes/Router'
import { ReportFilterRenderer } from './report-filter-renderer'
import { ReportConfig, ReportFilters } from '../helpers/reports-page-types'
import { useTranslation } from 'react-i18next'

interface ReportAccordionItemProps {
  report: ReportConfig
  dateFilter: ReportFilters
  expanded: boolean
  onToggle: (id: string) => void
}

export const ReportAccordionItem = ({ report, dateFilter, expanded, onToggle }: ReportAccordionItemProps) => {
  const { t } = useTranslation()
  const { createAlert } = useContext(AlertContext)
  const [filterValues, setFilterValues] = useState<Record<string, unknown>>(
    () => {
      const initial: Record<string, unknown> = {}
      report.filters.forEach((f) => {
        if (f.defaultValue !== undefined) {
          initial[f.name] = f.defaultValue
        }
      })
      return initial
    }
  )
  const [downloading, setDownloading] = useState(false)

  useEffect(() => {
    const initial: Record<string, unknown> = {};
    report.filters.forEach((f) => {
      if (f.defaultValue !== undefined) {
        initial[f.name] = f.defaultValue
      }
    })
    setFilterValues(initial)
  }, [report.filters])

  const handleFilterChange = useCallback((name: string, value: unknown) => {
    setFilterValues((prev) => ({ ...prev, [name]: value }))
  }, [])

  const handleDownload = useCallback(async () => {
    const reportsRequireDates = ['reports:dashboard', 'reports:visitors']

    if (reportsRequireDates.includes(report.id)) {
      if (!dateFilter.startDate || !dateFilter.endDate) {
        createAlert(ROUTES.REPORTS, t('reports.page.date.error'), 'error')
        return;
      }
    }

    if (!dateFilter.format) {
      createAlert(ROUTES.REPORTS, t('reports.page.format.error'), 'error')
      return;
    }

    const missingRequired = report.filters
      .filter((f) => f.required)
      .find((f) => {
        const val = filterValues[f.name]
        return val === undefined || val === null || val === ''
      })

    if (missingRequired) {
      createAlert(ROUTES.REPORTS, `${missingRequired.label} ${t('reports.page.required.field.error')}`, 'error')
      return;
    }

    setDownloading(true);
    try {
      await report.onDownload(dateFilter, filterValues)
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : t('reports.page.general.error')
      createAlert(ROUTES.REPORTS, message, 'error')
    } finally {
      setDownloading(false)
    }
  }, [dateFilter, filterValues, report])

  return (
    <Accordion
      expanded={expanded}
      onChange={() => onToggle(report.id)}
      sx={{
        '&:before': { display: 'none' },
        boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
        borderRadius: '8px !important',
        mb: 1.5,
        overflow: 'hidden',
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{
          minHeight: 56,
          '& .MuiAccordionSummary-content': {
            alignItems: 'center',
            gap: 1.5,
          },
        }}
      >
        {report.icon && (
          <Box sx={{ color: 'primary.main', display: 'flex' }}>
            {report.icon}
          </Box>
        )}
        <Box>
          <Typography variant="subtitle1" fontWeight={600}>
            {report.name}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {report.description}
          </Typography>
        </Box>
      </AccordionSummary>

      <AccordionDetails>
        {report.filters.length > 0 && (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, 1fr)',
                md: 'repeat(3, 1fr)',
              },
              gap: 2,
              mb: 2,
            }}
          >
            {report.filters.map((filter) => (
              <ReportFilterRenderer
                key={filter.name}
                filter={filter}
                value={filterValues[filter.name]}
                onChange={handleFilterChange}
              />
            ))}
          </Box>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="contained"
            startIcon={
              downloading ? (
                <CircularProgress size={18} color="inherit" />
              ) : (
                <DownloadIcon />
              )
            }
            onClick={handleDownload}
            disabled={downloading}
          >
            {downloading ? t('reports.page.await.dowload.button') : t('reports.page.download.button')}
          </Button>
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};
